import { Alert, Button, Col, Form, Grid, Input, Row, Typography, message } from "antd";
import { setAuthTokens } from "axios-jwt";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    PasswordResetEmailResponse,
    getSelf,
    requestPasswordReset,
    setPassword,
} from "../../networking/auth";
import { LOGIN_SUCCESS } from "../../redux/actions/types";
import { useAppDispatch } from "../../redux/hooks";

const { Title, Text } = Typography;

type Step = "request" | "reset";

export default function ForgotPassword() {
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.lg;
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const [step, setStep] = useState<Step>("request");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [resetData, setResetData] = useState<PasswordResetEmailResponse | null>(null);

    const [requestForm] = Form.useForm();
    const [resetForm] = Form.useForm();

    const handleRequestReset = async ({ email }: { email: string }) => {
        setLoading(true);
        setError(null);
        try {
            const response = await requestPasswordReset({ email });
            setResetData(response.data);
            setStep("reset");
            message.success("Reset token sent to your email");
        } catch (err: any) {
            setError(
                err.response?.data?.error ||
                    "Failed to send reset email. Please check your email address."
            );
        } finally {
            setLoading(false);
        }
    };

    const handleSetPassword = async ({
        token,
        password,
    }: {
        token: string;
        password: string;
    }) => {
        if (!resetData) return;

        setLoading(true);
        setError(null);
        try {
            const response = await setPassword(resetData.pk, {
                email: resetData.email,
                password,
                token,
            });
            // Save tokens and log user in automatically
            setAuthTokens({
                accessToken: response.data.access,
                refreshToken: response.data.refresh,
            });
            // Fetch user details and update Redux store (same as login flow)
            const userResponse = await getSelf();
            dispatch({ type: LOGIN_SUCCESS, payload: userResponse.data });
            message.success("Password set successfully");
            navigate("/home");
        } catch (err: any) {
            setError(
                err.response?.data?.error || "Failed to reset password. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };

    const colProps = {
        xxl: { span: 14, offset: 3 },
        xl: { span: 14, offset: 3 },
        lg: { span: 15, offset: 2 },
        md: { span: 20, offset: 2 },
        sm: { span: 24 },
        xs: { span: 24 },
    };

    const formLabelCol = {
        xs: { span: 0 },
        sm: { span: 0 },
        md: { span: 12 },
        lg: { span: 8 },
        xl: { span: 8 },
        xxl: { span: 8 },
    };

    const formWrapperCol = {
        xs: { span: 24 },
        sm: { span: 24 },
        md: { span: 12, offset: !screens.lg ? 6 : 0 },
        lg: { span: 16 },
        xl: { span: 16 },
        xxl: { span: 16 },
    };

    const buttonWrapperCol = {
        xs: { span: 24 },
        sm: { span: 24 },
        md: { span: 24, offset: !screens.lg ? 6 : 12 },
        lg: { span: 12, offset: 8 },
        xl: { span: 12, offset: 8 },
        xxl: { span: 12, offset: 8 },
    };

    return (
        <div
            style={{
                paddingTop: 24,
                paddingBottom: 24,
                paddingLeft: isMobile ? 24 : 0,
                paddingRight: isMobile ? 24 : 0,
            }}
        >
            <Row>
                <Col {...colProps}>
                    <Title level={2} style={{ textAlign: isMobile ? "center" : "start" }}>
                        Reset password
                    </Title>
                </Col>
            </Row>

            {step === "request" && (
                <>
                    <Row>
                        <Col {...colProps}>
                            <Text type="secondary">
                                Enter your email address and we'll send you a token to reset your
                                password.
                            </Text>
                        </Col>
                    </Row>
                    <Row style={{ marginTop: 16 }}>
                        <Col {...colProps}>
                            <Form
                                form={requestForm}
                                name="password-reset-request"
                                onFinish={handleRequestReset}
                                labelCol={formLabelCol}
                                wrapperCol={formWrapperCol}
                                onFocus={() => setError(null)}
                            >
                                <Form.Item
                                    label={isMobile ? null : "Email"}
                                    name="email"
                                    rules={[
                                        {
                                            required: true,
                                            type: "email",
                                            message: "Please enter a valid email address",
                                        },
                                    ]}
                                >
                                    <Input
                                        placeholder="your@email.com"
                                        type="email"
                                        autoComplete="email"
                                    />
                                </Form.Item>
                                <Form.Item wrapperCol={buttonWrapperCol}>
                                    <Button
                                        block={isMobile}
                                        type="primary"
                                        htmlType="submit"
                                        loading={loading}
                                    >
                                        Send reset token
                                    </Button>
                                </Form.Item>
                            </Form>
                        </Col>
                    </Row>
                </>
            )}

            {step === "reset" && resetData && (
                <>
                    <Row>
                        <Col {...colProps}>
                            <Text type="secondary">
                                We sent a reset token to {resetData.email}. Enter the token and your
                                new password below.
                            </Text>
                        </Col>
                    </Row>
                    <Row style={{ marginTop: 16 }}>
                        <Col {...colProps}>
                            <Form
                                form={resetForm}
                                name="password-reset-form"
                                onFinish={handleSetPassword}
                                labelCol={formLabelCol}
                                wrapperCol={formWrapperCol}
                                onFocus={() => setError(null)}
                            >
                                <Form.Item
                                    label={isMobile ? null : "Reset token"}
                                    name="token"
                                    rules={[
                                        {
                                            required: true,
                                            message: "Please enter the reset token from your email",
                                        },
                                    ]}
                                >
                                    <Input placeholder="Enter token from email" autoComplete="off" />
                                </Form.Item>
                                <Form.Item
                                    label={isMobile ? null : "New password"}
                                    name="password"
                                    rules={[
                                        {
                                            required: true,
                                            message: "Please enter your new password",
                                        },
                                        {
                                            min: 8,
                                            message: "Password must be at least 8 characters",
                                        },
                                        {
                                            pattern: /^(?=.*[a-z])(?=.*\d)/,
                                            message: "Password must contain at least one lowercase letter and one digit",
                                        },
                                    ]}
                                >
                                    <Input.Password
                                        placeholder="New password"
                                        autoComplete="new-password"
                                    />
                                </Form.Item>
                                <Form.Item
                                    label={isMobile ? null : "Confirm password"}
                                    name="confirmPassword"
                                    dependencies={["password"]}
                                    rules={[
                                        {
                                            required: true,
                                            message: "Please confirm your password",
                                        },
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                if (!value || getFieldValue("password") === value) {
                                                    return Promise.resolve();
                                                }
                                                return Promise.reject(
                                                    new Error("Passwords do not match")
                                                );
                                            },
                                        }),
                                    ]}
                                >
                                    <Input.Password
                                        placeholder="Confirm new password"
                                        autoComplete="new-password"
                                    />
                                </Form.Item>
                                <Form.Item wrapperCol={buttonWrapperCol}>
                                    <Button
                                        block={isMobile}
                                        type="primary"
                                        htmlType="submit"
                                        loading={loading}
                                    >
                                        Reset password
                                    </Button>
                                </Form.Item>
                            </Form>
                        </Col>
                    </Row>
                    <Row style={{ marginTop: 8 }}>
                        <Col {...colProps}>
                            <Button type="link" style={{ padding: 0 }} onClick={() => setStep("request")}>
                                ← Use a different email
                            </Button>
                        </Col>
                    </Row>
                </>
            )}

            {error && (
                <Row style={{ marginTop: 16 }}>
                    <Col {...colProps}>
                        <Alert message={error} type="error" />
                    </Col>
                </Row>
            )}

            <Row style={{ marginTop: 16 }}>
                <Col {...colProps}>
                    <span>Remember your password?&nbsp;</span>
                    <Link to="/login">
                        <span>Log in</span>
                    </Link>
                </Col>
            </Row>
        </div>
    );
}

